import { useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";
import { api } from "@/lib/api";

let cachedInfo = null;

export function usePypiInfo() {
  const [info, setInfo] = useState(cachedInfo);
  useEffect(() => {
    if (cachedInfo) return;
    api
      .get("/pypi-version")
      .then((r) => {
        cachedInfo = r.data;
        setInfo(r.data);
      })
      .catch(() => {});
  }, []);
  return info;
}

export function useUpiiVersion() {
  const info = usePypiInfo();
  return info?.version || null;
}

export const VersionBadge = ({ className = "" }) => {
  const v = useUpiiVersion();
  return (
    <span className={`inline-flex items-center gap-2 font-mono text-xs ${className}`} data-testid="version-badge">
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full rounded-full bg-signal opacity-60 animate-ping" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
      </span>
      <span className="text-signal">v{v || "0.7.0"}</span>
      <span className="text-mist">· Live on PyPI</span>
    </span>
  );
};

export const PypiStats = ({ className = "", light = false }) => {
  const info = usePypiInfo();
  const released = info?.released
    ? new Date(info.released).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : null;
  const dl = info?.downloads;
  const muted = light ? "text-ink/50" : "text-mist";
  const strong = light ? "text-ink" : "text-bone";
  return (
    <div className={`flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-xs ${className}`} data-testid="pypi-stats">
      {typeof dl === "number" && dl > 0 && (
        <span className={muted}>
          <span className={strong}>{dl.toLocaleString()}</span> downloads · last 30 days
        </span>
      )}
      {released && (
        <span className={muted}>
          last release <span className={strong}>{released}</span>
        </span>
      )}
    </div>
  );
};

export const CopyInstall = ({ variant = "block", className = "" }) => {
  const [copied, setCopied] = useState(false);
  const cmd = "pip install upii";
  const copy = () => {
    try {
      const p = navigator.clipboard?.writeText(cmd);
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch (_) {
      /* clipboard unavailable — ignore */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  if (variant === "nav") {
    return (
      <button
        onClick={copy}
        data-testid="nav-copy-install"
        className="group hidden items-center gap-2 border border-white/12 px-3 py-1.5 font-mono text-xs text-mist transition-colors duration-300 hover:border-saffron/50 hover:text-bone lg:inline-flex"
      >
        <span className="text-saffron">$</span>
        <span>pip install upii</span>
        {copied ? <Check className="h-3.5 w-3.5 text-signal" /> : <Copy className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100" />}
      </button>
    );
  }

  if (variant === "inline") {
    return (
      <button
        onClick={copy}
        data-testid="copy-install-inline"
        className={`group inline-flex items-center gap-3 border border-white/12 bg-white/[0.02] px-4 py-2.5 font-mono text-sm text-bone transition-colors duration-300 hover:border-saffron/40 ${className}`}
      >
        <span className="text-saffron">$</span>
        <span>{cmd}</span>
        {copied ? <Check className="h-4 w-4 text-signal" /> : <Copy className="h-4 w-4 text-mist group-hover:text-bone" />}
      </button>
    );
  }

  // block
  return (
    <div className={`w-full ${className}`}>
      <button
        onClick={copy}
        data-testid="copy-install-block"
        className="group flex w-full items-center justify-between gap-6 border border-white/12 bg-white/[0.02] px-5 py-4 text-left font-mono text-sm text-bone transition-colors duration-300 hover:border-saffron/40"
      >
        <span className="flex items-center gap-3">
          <span className="text-saffron">$</span>
          <span>{cmd}</span>
        </span>
        <span className="inline-flex items-center gap-2 text-xs text-mist group-hover:text-bone">
          {copied ? (
            <>
              <Check className="h-4 w-4 text-signal" /> copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" /> copy
            </>
          )}
        </span>
      </button>
      <div className="mt-3">
        <VersionBadge />
      </div>
    </div>
  );
};
